import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Bath, BedDouble, Building2, Car, ChevronLeft, ChevronRight, MapPin, MessageCircle, Ruler, Send, ExternalLink, Search, Clock3, Loader2 } from 'lucide-react';
import PublicLandingAiBubble from '@/components/PublicLandingAiBubble';
import { normalizePhone } from '@/lib/phoneUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (price, currency = 'MXN') => new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price || 0);

function TravelTimeDialog({ open, onOpenChange, destinationLabel, destinationMapsUrl }) {
  const [origin, setOrigin] = useState('');
  const [mode, setMode] = useState('driving');

  const openGoogleMaps = () => {
    if (!origin.trim()) {
      toast.error('Ingresa un punto de origen');
      return;
    }
    const destination = destinationMapsUrl || destinationLabel;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=${mode}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white border-stone-200" data-testid="travel-time-dialog">
        <DialogHeader>
          <DialogTitle className="text-stone-900 flex items-center gap-2"><Clock3 className="w-5 h-5" /> Calcular tiempo de recorrido</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 text-sm text-stone-600">
            Destino: <strong className="text-stone-900">{destinationLabel || 'Ubicación de la propiedad'}</strong>
          </div>
          <Input value={origin} onChange={e => setOrigin(e.target.value)} placeholder="Escribe tu dirección de origen" className="border-stone-300" data-testid="travel-origin-input" />
          <div className="flex gap-2 flex-wrap">
            {[
              ['driving', 'Auto'],
              ['walking', 'A pie'],
              ['transit', 'Transporte'],
            ].map(([value, label]) => (
              <Button key={value} variant={mode === value ? 'default' : 'outline'} onClick={() => setMode(value)} data-testid={`travel-mode-${value}`}>{label}</Button>
            ))}
          </div>
          <Button className="w-full" onClick={openGoogleMaps} data-testid="travel-open-google-maps">Abrir ruta en Google Maps</Button>
          <p className="text-xs text-stone-500">Google Maps te mostrará el tiempo estimado y la ruta exacta en una nueva pestaña.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RelatedPropertyCard({ property }) {
  const img = property.images?.[0];
  const imgUrl = img ? (img.startsWith('http') ? img : `${API}${img}`) : null;
  return (
    <Link to={`/propiedad/${property.company?.slug}/${property.id}`} className="block" data-testid={`related-property-${property.id}`}>
      <Card className="border-stone-200 bg-white hover:-translate-y-1 hover:shadow-lg transition-all">
        <div className="aspect-[16/11] bg-stone-100 overflow-hidden">{imgUrl ? <img src={imgUrl} alt={property.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-8 h-8 text-stone-300" /></div>}</div>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-stone-900 line-clamp-2">{property.title}</p>
            {property.is_sponsored_public && <Badge className="bg-amber-200 text-stone-900 border-0 text-[9px]">Patrocinada</Badge>}
          </div>
          <p className="text-xs text-stone-500">{property.zone || property.city || property.state}</p>
          <p className="text-lg font-bold text-stone-900">{fmt(property.price, property.currency)}</p>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function MarketplacePropertyPage() {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageIndex, setImageIndex] = useState(0);
  const [mapsOpen, setMapsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/marketplace/properties/${propertyId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [propertyId]);

  const filters = {
    q: searchParams.get('q') || '',
    property_type: searchParams.get('property_type') || '',
    operation_type: searchParams.get('operation_type') || '',
    zone: searchParams.get('zone') || '',
  };

  const whatsappLink = useMemo(() => {
    const phone = (data?.company?.contact_phone || '').replace(/[^0-9]/g, '');
    const text = encodeURIComponent(`Hola estoy interesado en "${data?.property?.title || 'esta propiedad'}"`);
    return phone ? `https://wa.me/${phone}?text=${text}` : '';
  }, [data]);

  const submit = async () => {
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error('Nombre y contacto son requeridos');
      return;
    }
    setSending(true);
    try {
      const normalizedPhone = form.phone ? normalizePhone(form.phone, '52', 'default') : '';
      const r = await fetch(`${API}/api/marketplace/properties/${propertyId}/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, phone: normalizedPhone }) });
      const result = await r.json();
      if (!r.ok) throw new Error(result.detail || 'Error');
      toast.success(result.message || 'Solicitud enviada');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (e) {
      toast.error(e.message);
    } finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><Loader2 className="w-8 h-8 animate-spin text-stone-900" /></div>;
  if (!data?.property) return <div className="min-h-screen flex items-center justify-center text-stone-900 bg-stone-50">Propiedad no encontrada.</div>;

  const { property, company, related_properties = [] } = data;
  const logoUrl = company.logo_url ? (company.logo_url.startsWith('http') ? company.logo_url : `${API}${company.logo_url}`) : null;
  const images = (property.images || []).map(img => (img?.startsWith('http') ? img : `${API}${img}`));
  const imgUrl = images[imageIndex] || null;
  const destinationLabel = property.address || `${property.zone || ''} ${property.city || ''} ${property.state || ''}`.trim();

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="marketplace-property-page">
      <div className="max-w-screen-2xl mx-auto px-5 lg:px-10 py-8 space-y-8">
        <div className="flex items-center justify-between gap-6 border-b border-stone-200 pb-6">
          <div className="flex items-center gap-4">
            {logoUrl ? <img src={logoUrl} alt={company.name} className="w-16 h-16 rounded-md object-contain border border-stone-200 bg-white p-2" /> : <div className="w-16 h-16 rounded-md bg-stone-950 text-white flex items-center justify-center font-bold">{company.name?.[0]}</div>}
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">{company.name}</p>
              <h1 className="text-3xl sm:text-5xl font-medium tracking-tight">{property.title}</h1>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">{fmt(property.price, property.currency)}</p>
            <p className="text-sm text-stone-500">{property.operation_type} · {property.property_type}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-8 items-start">
          <aside className="space-y-6 xl:sticky xl:top-6" data-testid="property-search-sidebar">
            <Card className="border-stone-200 bg-white shadow-none">
              <CardContent className="p-6 space-y-4">
                <h2 className="text-lg font-semibold">Nueva búsqueda</h2>
                <Input defaultValue={filters.q} placeholder="Buscar por zona o propiedad" className="border-stone-300" />
                <Input defaultValue={filters.zone} placeholder="Zona / colonia" className="border-stone-300" />
                <div className="grid grid-cols-2 gap-3">
                  <Input defaultValue={filters.property_type} placeholder="Tipo" className="border-stone-300" />
                  <Input defaultValue={filters.operation_type} placeholder="Operación" className="border-stone-300" />
                </div>
                <Link to={`/marketplace?zone=${encodeURIComponent(property.zone || property.city || '')}`}><Button className="w-full" data-testid="property-sidebar-search-btn"><Search className="w-4 h-4 mr-2" /> Buscar en la zona</Button></Link>
              </CardContent>
            </Card>

            <Card className="border-stone-200 bg-white shadow-none">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-stone-500" /><h3 className="font-semibold">Ubicación</h3></div>
                <p className="text-sm text-stone-600">{destinationLabel || 'Ubicación disponible'}</p>
                {property.google_maps_url && <a href={property.google_maps_url} target="_blank" rel="noreferrer"><Button variant="outline" className="w-full" data-testid="property-open-maps">Ver ubicación</Button></a>}
                <Button className="w-full" onClick={() => setMapsOpen(true)} data-testid="property-open-travel-modal">Calcular tiempo de recorrido</Button>
              </CardContent>
            </Card>
          </aside>

          <main className="space-y-8">
            <div className="space-y-4">
              <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-stone-200 border border-stone-200 relative">
                {imgUrl ? <img src={imgUrl} alt={property.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-12 h-12 text-stone-400" /></div>}
                {images.length > 1 && (
                  <>
                    <button type="button" onClick={() => setImageIndex(i => (i - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/85 text-stone-900 flex items-center justify-center" data-testid="property-carousel-prev"><ChevronLeft className="w-5 h-5" /></button>
                    <button type="button" onClick={() => setImageIndex(i => (i + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/85 text-stone-900 flex items-center justify-center" data-testid="property-carousel-next"><ChevronRight className="w-5 h-5" /></button>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="property-carousel-thumbs">
                  {images.map((src, idx) => (
                    <button key={src + idx} type="button" onClick={() => setImageIndex(idx)} className={`aspect-video rounded-xl overflow-hidden border ${idx === imageIndex ? 'border-stone-900' : 'border-stone-200'}`}>
                      <img src={src} alt={`${property.title}-${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {property.bedrooms > 0 && <Card className="border-stone-200 bg-white"><CardContent className="p-5 text-center"><BedDouble className="w-4 h-4 mx-auto mb-2 text-stone-500" /><p className="text-2xl font-semibold">{property.bedrooms}</p><p className="text-xs text-stone-500 uppercase tracking-[0.18em]">Recámaras</p></CardContent></Card>}
              {property.bathrooms > 0 && <Card className="border-stone-200 bg-white"><CardContent className="p-5 text-center"><Bath className="w-4 h-4 mx-auto mb-2 text-stone-500" /><p className="text-2xl font-semibold">{property.bathrooms}</p><p className="text-xs text-stone-500 uppercase tracking-[0.18em]">Baños</p></CardContent></Card>}
              {property.parking_spots > 0 && <Card className="border-stone-200 bg-white"><CardContent className="p-5 text-center"><Car className="w-4 h-4 mx-auto mb-2 text-stone-500" /><p className="text-2xl font-semibold">{property.parking_spots}</p><p className="text-xs text-stone-500 uppercase tracking-[0.18em]">Estac.</p></CardContent></Card>}
              {property.area_m2 > 0 && <Card className="border-stone-200 bg-white"><CardContent className="p-5 text-center"><Ruler className="w-4 h-4 mx-auto mb-2 text-stone-500" /><p className="text-2xl font-semibold">{property.area_m2}</p><p className="text-xs text-stone-500 uppercase tracking-[0.18em]">m²</p></CardContent></Card>}
            </div>

            <Card className="border-stone-200 bg-white shadow-none"><CardContent className="p-8 space-y-6"><div><p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-2">Descripción</p><p className="text-lg leading-relaxed text-stone-600 whitespace-pre-line">{property.description || 'Sin descripción adicional.'}</p></div>{property.amenities?.length > 0 && <div><p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-3">Amenidades</p><div className="flex flex-wrap gap-2">{property.amenities.map(item => <Badge key={item} className="rounded-full bg-stone-100 text-stone-800 border-0 px-4 py-2">{item}</Badge>)}</div></div>}{property.characteristics?.length > 0 && <div><p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-3">Características</p><div className="flex flex-wrap gap-2">{property.characteristics.map(item => <Badge key={item} className="rounded-full bg-stone-100 text-stone-800 border-0 px-4 py-2">{item}</Badge>)}</div></div>}</CardContent></Card>

            <Card className="border-stone-200 bg-stone-950 text-stone-50 shadow-none"><CardContent className="p-8 space-y-5"><div><p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mb-2">Solicita información</p><h2 className="text-2xl font-semibold">{company.name}</h2><p className="text-sm text-stone-400">Elige tu canal preferido y te damos seguimiento.</p></div>{whatsappLink && <a href={whatsappLink} target="_blank" rel="noreferrer"><Button className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white" data-testid="property-whatsapp-cta"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp de la inmobiliaria</Button></a>}<div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre" data-testid="property-contact-name" className="border-stone-700 bg-stone-900 text-white" /><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono" data-testid="property-contact-phone" className="border-stone-700 bg-stone-900 text-white" /></div><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="property-contact-email" className="border-stone-700 bg-stone-900 text-white" /><Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={`Hola estoy interesado en "${property.title}"`} data-testid="property-contact-message" className="border-stone-700 bg-stone-900 text-white min-h-[120px]" /><Button onClick={submit} disabled={sending} className="w-full bg-white text-stone-950 hover:bg-stone-100" data-testid="property-contact-submit">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Enviar solicitud</>}</Button></CardContent></Card>

            {related_properties.length > 0 && (
              <section className="space-y-4" data-testid="related-properties-section">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Zona / patrocinadas</p>
                  <h2 className="text-3xl font-semibold">Otras propiedades destacadas</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {related_properties.map(item => <RelatedPropertyCard key={item.id} property={item} />)}
                </div>
              </section>
            )}
          </main>
        </div>
      </div>

      <TravelTimeDialog open={mapsOpen} onOpenChange={setMapsOpen} destinationLabel={destinationLabel} destinationMapsUrl={property.google_maps_url} />
      <PublicLandingAiBubble endpoint={`${API}/api/marketplace/chat`} intro={`Hola, soy la IA de ${company.name}. Puedo ayudarte con ${property.title}.`} placeholder="Pregunta por precio, disponibilidad o amenidades..." />
    </div>
  );
}